import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';
import { addExemptionRule, fetchExemptionRules, updateExemptionRule, deleteExemptionRule } from '../api/exemptionRuleApi';
import { fetchApartments } from '../api/apartmentApi';
import { fetchPeople } from '../api/personApi';
import { fetchDuesDefinitions } from '../api/duesDefinitionApi';
import { fetchBlocks } from '../api/blockApi';
import '../styles/CommonStyles.css';

const ExemptionRulesView = ({ showToast, currentUser }) => {
  const [exemptionRules, setExemptionRules] = useState([]);
  const [exemptionRuleForm, setExemptionRuleForm] = useState({
    apartmentId: '',
    personId: '',
    duesDefinitionId: '',
    exemptionType: 'tamMuaf',
    exemptAmount: '',
  });
  const [apartments, setApartments] = useState([]);
  const [people, setPeople] = useState([]);
  const [duesDefinitions, setDuesDefinitions] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, ruleId: null });

  const loadInitialData = useCallback(async () => {
    setIsDataLoading(true);
    try {
      console.log('Başlangıç verileri yükleniyor...');
      const [apts, ppl, defs, blks] = await Promise.all([
        fetchApartments({ buildingId: currentUser.buildingId }),
        fetchPeople(currentUser.buildingId),
        fetchDuesDefinitions(currentUser.buildingId),
        fetchBlocks(currentUser.buildingId),
      ]);
      
      console.log('Daireler:', apts);
      console.log('Kişiler:', ppl);
      console.log('Aidat Tanımları:', defs);
      console.log('Bloklar:', blks);
      
      const normalizedApts = Array.isArray(apts) ? apts : apts.recordset ?? apts.data ?? apts.apartments ?? [];
      const normalizedPpl = Array.isArray(ppl) ? ppl : ppl.recordset ?? ppl.data ?? [];
      const normalizedDefs = Array.isArray(defs) ? defs : defs.recordset ?? defs.data ?? [];
      const normalizedBlks = Array.isArray(blks) ? blks : blks.recordset ?? blks.data ?? [];
      
      setApartments(normalizedApts);
      setPeople(normalizedPpl);
      setDuesDefinitions(normalizedDefs);
      setBlocks(normalizedBlks);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      showToast("Gerekli veriler yüklenirken hata: " + error.message, "error");
    } finally {
      setIsDataLoading(false);
    }
  }, [showToast, currentUser]);

  const loadExemptionRules = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Muafiyet kuralları yükleniyor, buildingId:', currentUser.buildingId);
      const data = await fetchExemptionRules(currentUser.buildingId);
      const normalizedData = Array.isArray(data) ? data : data?.recordset ?? data?.data ?? [];
      console.log('Alınan muafiyet kuralları:', normalizedData);
      if (normalizedData.length > 0) {
        console.log('Örnek muafiyet kuralı:', normalizedData[0]);
      }
      setExemptionRules(normalizedData);
    } catch (error) {
      console.error('Muafiyet kuralları yükleme hatası:', error);
      showToast("Muafiyet kuralları yüklenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!isDataLoading) {
      loadExemptionRules();
    }
  }, [isDataLoading, loadExemptionRules]);

  const getBlockName = (blockId) => {
    if (!blockId || !blocks.length) return 'Bilinmiyor';
    const block = blocks.find(blk => {
      const blkId = blk.id || blk.blockId || blk.BlockId;
      const compareId = parseInt(blockId);
      return blkId === compareId;
    });
    if (block) {
      const blkName = block.name || block.Name;
      return blkName || 'Bilinmiyor';
    } else {
      return 'Bilinmiyor';
    }
  };

  const getBlockIdForApartment = (apartmentId) => {
    if (!apartmentId || !apartments.length) return null;
    const apt = apartments.find(apt => {
      const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
      const compareId = parseInt(apartmentId);
      return aptId === compareId;
    });
    if (apt) {
      const blkId = apt.blockId || apt.BlockId;
      return blkId;
    } else {
      return null;
    }
  };

  const getApartmentNumber = (apartmentId) => {
    if (!apartmentId || !apartments.length) return 'Tüm Daireler';
    const apt = apartments.find(apt => {
      const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
      const compareId = parseInt(apartmentId);
      return aptId === compareId;
    });
    if (apt) {
      const aptNumber = apt.apartmentNumber || apt.ApartmentNumber;
      const blockId = getBlockIdForApartment(apt.id || apt.apartmentId || apt.ApartmentId); // Ensure we use the correct ID from apt
      const blockName = getBlockName(blockId);
      return `${aptNumber} (${blockName})`;
    } else {
      return 'Bilinmiyor';
    }
  };

  const getPersonName = (personId) => {
    if (!personId || !people.length) return 'Tüm Kişiler';
    const person = people.find(p => {
      const pId = p.id || p.personId || p.PersonId;
      const compareId = parseInt(personId);
      return pId === compareId;
    });
    if (person) {
      const personName = person.name || person.Name;
      return personName || 'Bilinmiyor';
    } else {
      return 'Bilinmiyor';
    }
  };

  const getDuesDefinitionName = (duesDefId) => {
    if (!duesDefId || !duesDefinitions.length) return 'Tüm Aidatlar';
    const duesDef = duesDefinitions.find(def => {
      const defId = def.id || def.duesDefinitionId || def.DuesDefinitionId;
      const compareId = parseInt(duesDefId);
      return defId === compareId;
    });
    if (duesDef) {
      const defName = duesDef.name || duesDef.Name;
      return defName || 'Bilinmiyor';
    } else {
      return 'Bilinmiyor';
    }
  };

  const handleAddExemptionRule = async (e) => {
    e.preventDefault();
    const { apartmentId, personId, duesDefinitionId, exemptionType, exemptAmount } = exemptionRuleForm;
    if (exemptionType === 'kismiMuaf' && (isNaN(parseFloat(exemptAmount)) || parseFloat(exemptAmount) <= 0)) {
      showToast("Kısmi muafiyet için geçerli bir tutar girin.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const ruleData = {
        buildingId: currentUser.buildingId,
        apartmentId: apartmentId ? parseInt(apartmentId) : null,
        personId: personId ? parseInt(personId) : null,
        duesDefinitionId: duesDefinitionId ? parseInt(duesDefinitionId) : null,
        exemptionType,
        exemptAmount: exemptionType === 'kismiMuaf' ? parseFloat(exemptAmount) : null,
      };
      await addExemptionRule(ruleData);
      showToast('Muafiyet kuralı başarıyla eklendi.', 'success');
      setExemptionRuleForm({ apartmentId: '', personId: '', duesDefinitionId: '', exemptionType: 'tamMuaf', exemptAmount: '' });
      loadExemptionRules();
    } catch (error) {
      showToast('Muafiyet kuralı eklenirken hata: ' + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (rule) => {
    const exemptionId = rule.ExemptionId !== undefined ? rule.ExemptionId : rule.exemptionId;
    const apartmentId = rule.ApartmentId !== undefined ? rule.ApartmentId : rule.apartmentId;
    const personId = rule.PersonId !== undefined ? rule.PersonId : rule.personId;
    const duesDefinitionId = rule.DuesDefinitionId !== undefined ? rule.DuesDefinitionId : rule.duesDefinitionId;
    const exemptionType = rule.ExemptionType !== undefined ? rule.ExemptionType : rule.exemptionType;
    const exemptAmount = rule.ExemptAmount !== undefined ? rule.ExemptAmount : rule.exemptAmount;
    setEditingRule({
      ...rule,
      exemptionId: exemptionId,
      apartmentId: apartmentId !== null ? String(apartmentId) : '',
      personId: personId !== null ? String(personId) : '',
      duesDefinitionId: duesDefinitionId !== null ? String(duesDefinitionId) : '',
      exemptionType: exemptionType,
      exemptAmount: exemptAmount !== null ? String(exemptAmount) : '',
    });
    setEditModalOpen(true);
  };

  const handleUpdateExemptionRule = async (e) => {
    e.preventDefault();
    if (!editingRule) return;
    const exemptionId = editingRule.ExemptionId !== undefined ? editingRule.ExemptionId : editingRule.exemptionId;
    const { apartmentId, personId, duesDefinitionId, exemptionType, exemptAmount } = editingRule;
    if (exemptionType === 'kismiMuaf' && (isNaN(parseFloat(exemptAmount)) || parseFloat(exemptAmount) <= 0)) {
      showToast("Kısmi muafiyet için geçerli bir tutar girin.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const updatedData = {
        buildingId: currentUser.buildingId,
        apartmentId: apartmentId ? parseInt(apartmentId) : null,
        personId: personId ? parseInt(personId) : null,
        duesDefinitionId: duesDefinitionId ? parseInt(duesDefinitionId) : null,
        exemptionType,
        exemptAmount: exemptionType === 'kismiMuaf' ? parseFloat(exemptAmount) : null,
      };
      await updateExemptionRule(exemptionId, updatedData);
      showToast('Muafiyet kuralı başarıyla güncellendi.', 'success');
      setEditModalOpen(false);
      setEditingRule(null);
      loadExemptionRules();
    } catch (error) {
      showToast('Muafiyet kuralı güncellenirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteExemptionRule = async () => {
    if (!confirmDeleteModal.ruleId) {
      showToast('Geçerli bir muafiyet kuralı ID\'si bulunamadı', 'error');
      setConfirmDeleteModal({ isOpen: false, ruleId: null });
      return;
    }
    setIsLoading(true);
    try {
      await deleteExemptionRule(confirmDeleteModal.ruleId);
      showToast('Muafiyet kuralı başarıyla silindi.', 'success');
      setConfirmDeleteModal({ isOpen: false, ruleId: null });
      loadExemptionRules();
    } catch (error) {
      showToast('Muafiyet kuralı silinirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isDataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Veriler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-gray-700">İşlem yapılıyor...</p>
        </div>
      )}
      <h1 className="text-2xl font-semibold mb-6">Muafiyet Kuralları Yönetimi</h1>

      <div className="mb-6 p-4 border rounded-lg bg-gray-50">
        <h2 className="text-xl font-semibold mb-4">Yeni Muafiyet Kuralı Ekle</h2>
        <form onSubmit={handleAddExemptionRule}>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="apartmentId" className="label-form-style">Daire (Opsiyonel)</label>
              <select
                id="apartmentId"
                name="apartmentId"
                value={exemptionRuleForm.apartmentId}
                onChange={(e) => setExemptionRuleForm({ ...exemptionRuleForm, apartmentId: e.target.value })}
                className="input-form-style"
              >
                <option value="">Tüm Daireler</option>
                {apartments
                  .filter(apt => {
                    const aptBuildingId = apt.buildingId || apt.BuildingId;
                    return aptBuildingId === currentUser.buildingId;
                  })
                  .map(apt => {
                    const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
                    return (
                      <option key={aptId} value={aptId}>{getApartmentNumber(aptId)}</option>
                    );
                  })}
              </select>
            </div>
            <div>
              <label htmlFor="personId" className="label-form-style">Kişi (Opsiyonel)</label>
              <select
                id="personId"
                name="personId"
                value={exemptionRuleForm.personId}
                onChange={(e) => setExemptionRuleForm({ ...exemptionRuleForm, personId: e.target.value })}
                className="input-form-style"
              >
                <option value="">Tüm Kişiler</option>
                {people
                  .filter(p => {
                    const pBuildingId = p.buildingId || p.BuildingId;
                    return pBuildingId === currentUser.buildingId;
                  })
                  .map(p => {
                    const pId = p.id || p.personId || p.PersonId;
                    const pName = p.name || p.Name;
                    return (
                      <option key={pId} value={pId}>{pName}</option>
                    );
                  })}
              </select>
            </div>
            <div>
              <label htmlFor="duesDefinitionId" className="label-form-style">Aidat Tanımı (Opsiyonel)</label>
              <select
                id="duesDefinitionId"
                name="duesDefinitionId"
                value={exemptionRuleForm.duesDefinitionId}
                onChange={(e) => setExemptionRuleForm({ ...exemptionRuleForm, duesDefinitionId: e.target.value })}
                className="input-form-style"
              >
                <option value="">Tüm Aidatlar</option>
                {duesDefinitions
                  .filter(def => {
                    const defBuildingId = def.buildingId || def.BuildingId;
                    return defBuildingId === currentUser.buildingId;
                  })
                  .map(def => {
                    const defId = def.id || def.duesDefinitionId || def.DuesDefinitionId;
                    const defName = def.name || def.Name;
                    return (
                      <option key={defId} value={defId}>{defName}</option>
                    );
                  })}
              </select>
            </div>
            <div>
              <label htmlFor="exemptionType" className="label-form-style">Muafiyet Türü</label>
              <select
                id="exemptionType"
                name="exemptionType"
                value={exemptionRuleForm.exemptionType}
                onChange={(e) => setExemptionRuleForm({ ...exemptionRuleForm, exemptionType: e.target.value })}
                className="input-form-style"
                required
              >
                <option value="tamMuaf">Tam Muaf</option>
                <option value="kismiMuaf">Kısmi Muaf</option>
              </select>
            </div>
            {exemptionRuleForm.exemptionType === 'kismiMuaf' && (
              <div>
                <label htmlFor="exemptAmount" className="label-form-style">Muaf Tutar (TL)</label>
                <input
                  type="number"
                  id="exemptAmount"
                  name="exemptAmount"
                  step="0.01"
                  value={exemptionRuleForm.exemptAmount}
                  onChange={(e) => setExemptionRuleForm({ ...exemptionRuleForm, exemptAmount: e.target.value })}
                  className="input-form-style"
                  required
                />
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-start">
            <button type="submit" className="btn-primary-green-style" disabled={isLoading}>
              <PlusCircle size={18} className="mr-2" /> Muafiyet Kuralı Ekle
            </button>
          </div>
        </form>
      </div>

      <h2 className="text-xl font-semibold mb-4">Mevcut Muafiyet Kuralları</h2>
      {exemptionRules.length === 0 && !isLoading ? (
        <p className="text-gray-500 text-center py-4">Henüz muafiyet kuralı bulunmamaktadır.</p>
      ) : (
        <div className="overflow-x-auto shadow border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Daire</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Kişi</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Aidat Tanımı</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Muafiyet Türü</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Muaf Tutar</th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {exemptionRules.map((rule) => {
                const exemptionId = rule.ExemptionId !== undefined ? rule.ExemptionId : rule.exemptionId;
                const apartmentId = rule.ApartmentId !== undefined ? rule.ApartmentId : rule.apartmentId;
                const personId = rule.PersonId !== undefined ? rule.PersonId : rule.personId;
                const duesDefinitionId = rule.DuesDefinitionId !== undefined ? rule.DuesDefinitionId : rule.duesDefinitionId;
                const exemptionType = rule.ExemptionType !== undefined ? rule.ExemptionType : rule.exemptionType;
                const exemptAmount = rule.ExemptAmount !== undefined ? rule.ExemptAmount : rule.exemptAmount;
                return (
                  <tr key={exemptionId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{getApartmentNumber(apartmentId)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{getPersonName(personId)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{getDuesDefinitionName(duesDefinitionId)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{exemptionType === 'tamMuaf' ? 'Tam Muaf' : 'Kısmi Muaf'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">{exemptAmount ? `${parseFloat(exemptAmount).toFixed(2)} TL` : '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center space-x-2">
                      <button onClick={() => openEditModal(rule)} className="icon-button-indigo" title="Düzenle">
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteModal({ isOpen: true, ruleId: exemptionId })}
                        className="icon-button-red"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingRule && (
        <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Muafiyet Kuralını Düzenle" size="lg">
          <form onSubmit={handleUpdateExemptionRule}>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="editApartmentId" className="label-form-style">Daire (Opsiyonel)</label>
                <select
                  id="editApartmentId"
                  name="apartmentId"
                  value={editingRule.apartmentId}
                  onChange={(e) => setEditingRule({ ...editingRule, apartmentId: e.target.value })}
                  className="input-form-style"
                >
                  <option value="">Tüm Daireler</option>
                  {apartments
                    .filter(apt => {
                      const aptBuildingId = apt.buildingId || apt.BuildingId;
                      return aptBuildingId === currentUser.buildingId;
                    })
                    .map(apt => {
                      const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
                      return (
                        <option key={aptId} value={aptId}>{getApartmentNumber(aptId)}</option>
                      );
                    })}
                </select>
              </div>
              <div>
                <label htmlFor="editPersonId" className="label-form-style">Kişi (Opsiyonel)</label>
                <select
                  id="editPersonId"
                  name="personId"
                  value={editingRule.personId}
                  onChange={(e) => setEditingRule({ ...editingRule, personId: e.target.value })}
                  className="input-form-style"
                >
                  <option value="">Tüm Kişiler</option>
                  {people
                    .filter(p => {
                      const pBuildingId = p.buildingId || p.BuildingId;
                      return pBuildingId === currentUser.buildingId;
                    })
                    .map(p => {
                      const pId = p.id || p.personId || p.PersonId;
                      const pName = p.name || p.Name;
                      return (
                        <option key={pId} value={pId}>{pName}</option>
                      );
                    })}
                </select>
              </div>
              <div>
                <label htmlFor="editDuesDefinitionId" className="label-form-style">Aidat Tanımı (Opsiyonel)</label>
                <select
                  id="editDuesDefinitionId"
                  name="duesDefinitionId"
                  value={editingRule.duesDefinitionId}
                  onChange={(e) => setEditingRule({ ...editingRule, duesDefinitionId: e.target.value })}
                  className="input-form-style"
                >
                  <option value="">Tüm Aidatlar</option>
                  {duesDefinitions
                    .filter(def => {
                      const defBuildingId = def.buildingId || def.BuildingId;
                      return defBuildingId === currentUser.buildingId;
                    })
                    .map(def => {
                      const defId = def.id || def.duesDefinitionId || def.DuesDefinitionId;
                      const defName = def.name || def.Name;
                      return (
                        <option key={defId} value={defId}>{defName}</option>
                      );
                    })}
                </select>
              </div>
              <div>
                <label htmlFor="editExemptionType" className="label-form-style">Muafiyet Türü</label>
                <select
                  id="editExemptionType"
                  name="exemptionType"
                  value={editingRule.exemptionType}
                  onChange={(e) => setEditingRule({ ...editingRule, exemptionType: e.target.value })}
                  className="input-form-style"
                  required
                >
                  <option value="tamMuaf">Tam Muaf</option>
                  <option value="kismiMuaf">Kısmi Muaf</option>
                </select>
              </div>
              {editingRule.exemptionType === 'kismiMuaf' && (
                <div>
                  <label htmlFor="editExemptAmount" className="label-form-style">Muaf Tutar (TL)</label>
                  <input
                    type="number"
                    id="editExemptAmount"
                    name="exemptAmount"
                    step="0.01"
                    value={editingRule.exemptAmount}
                    onChange={(e) => setEditingRule({ ...editingRule, exemptAmount: e.target.value })}
                    className="input-form-style"
                    required
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button type="button" onClick={() => setEditModalOpen(false)} className="btn-secondary-style px-4 py-2">
                İptal
              </button>
              <button type="submit" className="btn-primary-indigo-style px-4 py-2" disabled={isLoading}>
                Güncelle
              </button>
            </div>
          </form>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={confirmDeleteModal.isOpen}
        onClose={() => setConfirmDeleteModal({ isOpen: false, ruleId: null })}
        title="Muafiyet Kuralını Sil"
        message="Bu muafiyet kuralını silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        onConfirm={handleDeleteExemptionRule}
        confirmText="Evet, Sil"
        cancelText="İptal"
      />
    </div>
  );
};

export default ExemptionRulesView;
